<template>
  <section class="lead-new-lead-take ui-action-callout" :aria-busy="loading || pending ? 'true' : 'false'">
    <div class="lead-new-lead-take__content ui-action-callout__content">
      <span class="lead-new-lead-take__label ui-action-callout__label">New leads available</span>
      <strong v-if="!loading">{{ availableCount }}</strong>
      <span v-else class="lead-new-lead-take__skeleton ui-action-callout__skeleton" aria-hidden="true"></span>
      <p>Only requests matching your languages are included.</p>
    </div>

    <div class="lead-new-lead-take__actions ui-action-callout__actions">
      <AppButton
        v-if="error"
        design="new"
        theme="dark"
        type="button"
        class="lead-new-lead-take__retry ui-action-callout__retry"
        @click.native="retry"
      >
        Retry
      </AppButton>
      <AppButton
        design="new"
        type="button"
        :disabled="loading || pending || availableCount === 0"
        @click.native="take"
      >
        {{ pending ? 'Taking lead...' : 'Take in work' }}
      </AppButton>
    </div>

    <LeadFlowNewLeadsSocket
      v-if="snapshotReady"
      :reconcile="reconcile"
      @count-changed="applyCountEvent"
    />
  </section>
</template>

<script>
import LeadFlowNewLeadsSocket from '@/views/crm/task-tracker/LeadFlowNewLeadsSocket.vue'

export default {
  name: 'LeadNewLeadTakeControl',
  components: {
    LeadFlowNewLeadsSocket
  },
  props: {
    loadState: {
      type: Function,
      required: true
    },
    takeLead: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      availableCount: 0,
      loading: false,
      pending: false,
      error: null,
      snapshotReady: false,
      requestGeneration: 0
    }
  },
  created() {
    this.load(true).catch(() => {})
  },
  beforeDestroy() {
    this.requestGeneration += 1
  },
  methods: {
    retry() {
      this.load(true).catch(() => {})
    },
    async load(initial) {
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      if (initial) this.loading = true
      this.error = null

      try {
        const response = await this.loadState()
        if (generation !== this.requestGeneration) return
        const data = response.data || response
        this.availableCount = this.normalizeCount(data.available_count)
        this.snapshotReady = true
      } catch (error) {
        if (generation === this.requestGeneration) {
          this.error = error
          if (initial) this.snapshotReady = false
        }
        throw error
      } finally {
        if (generation === this.requestGeneration && initial) this.loading = false
      }
    },
    reconcile() {
      return this.load(false)
    },
    applyCountEvent() {
      // WebSocket only signals that the backlog changed. The REST snapshot is
      // authoritative because availability is specific to the current user.
      this.load(false).catch(() => {})
    },
    async take() {
      if (this.pending || this.availableCount === 0) return
      this.pending = true
      try {
        const response = await this.takeLead()
        const data = response.data || response
        this.$emit('assigned', data)
        await this.load(false)
      } catch (error) {
        const data = error && error.response ? error.response.data || {} : {}
        if (error && error.response && error.response.status === 400) {
          await this.load(false).catch(() => {})
          this.$notify({
            type: 'info',
            title: data.details || 'No matching New Leads are available'
          })
        } else {
          this.$notify({
            type: 'error',
            title: 'Unable to take a New Lead'
          })
        }
      } finally {
        this.pending = false
      }
    },
    normalizeCount(value) {
      const count = Number(value)
      return Number.isInteger(count) && count >= 0 ? count : 0
    }
  }
}
</script>
